import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import { useAppStore } from '@/stores/app'

// Create axios instance with default config for Laravel Sanctum
const api: AxiosInstance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 300000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  withCredentials: true, // Important for Sanctum to work with cookies
  withXSRFToken: true // Enable XSRF protection
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const appStore = useAppStore()
    appStore.setLoading(true)

    // Get token from localStorage for Sanctum bearer token
    const token = localStorage.getItem('token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Ensure we send proper headers for Laravel API
    if (config.headers) {
      config.headers['X-Requested-With'] = 'XMLHttpRequest'
    }

    return config
  },
  (error: AxiosError) => {
    const appStore = useAppStore()
    appStore.setLoading(false)
    console.error('Request error:', error)
    appStore.setError('Request failed')
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    const appStore = useAppStore()
    appStore.setLoading(false)
    return response
  },
  (error: AxiosError) => {
    const appStore = useAppStore()
    appStore.setLoading(false)
    console.error('Response error:', error)

    // Check for timeout errors
    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      appStore.setError('There is a problem with the network connection. Please try again.')
      return Promise.reject(error)
    }

    if (error.response) {
      console.error('Error response:', error.response.data)
      const status = error.response.status
      const baseUrl = process.env.VUE_APP_BASE_URL || '/'
      const loginPath = baseUrl.endsWith('/') ? `${baseUrl}login` : `${baseUrl}/login`
      
      switch (status) {
        case 401:
          // Handle unauthorized - clear token and redirect to login
          localStorage.removeItem('token')
          appStore.setError('Session expired. Please login again.')

          // Only redirect if not already on login page

          if (window.location.pathname !== loginPath) {
            window.location.href = loginPath
          }
          break
        case 403:
          appStore.setError('Access forbidden')
          break
        case 404:
          appStore.setError('Resource not found')
          break
        case 413: {
          const customMsg = (error.response.data as any)?.message || 'File upload exceeds the server limit of 32MB.'
          appStore.setError(customMsg)
          break
        }
        case 422: {
          // Laravel validation errors - don't set global error, let components handle it
          // The error will still be rejected and caught by the component
          break
        }
        case 429:
          appStore.setError('Too many requests. Please try again later.')
          break
        case 500:
          appStore.setError('Server error. Please try again.')
          break
        default:
          appStore.setError('An unexpected error occurred')
      }
    } else if (error.request) {
      console.error('No response received:', error.request)
      appStore.setError('There is a problem with the network connection. Please try again.')
    } else {
      console.error('Request setup error:', error.message)
      appStore.setError('Request failed to send')
    }

    return Promise.reject(error)
  }
)

export const sampleApi = {
  getMessage: async () => {
    try {
      const response = await api.get('/sample')
      return response.data
    } catch (error) {
      console.error('Sample API error:', error)
      throw error
    }
  }
}

export default api
